import { useLayoutEffect, useRef } from "react";

/**
 * Things in a list that move instead of jumping (first, last, invert, play).
 * Every render, each element marked with `attribute` is measured where it
 * now lies in the container's layout; one that lay somewhere else before
 * starts there and eases to its new place. A bot that a new message brings
 * to the top, or one dragged to another spot, slides there, and the ones it
 * passes slide out of its way.
 *
 * Places are layout offsets inside the container (which must be positioned),
 * so a scroll, or a transform still playing, is not a move. A container that
 * changed width, such as a sidebar being resized, just takes its new layout.
 *
 * Given `ids`, the list's own record of what is in it, it also greets and
 * sees off: an id new to the list grows in, and one gone from it leaves a
 * copy of itself shrinking where it stood (React has already taken it out).
 * An element that only came back into view, such as the pinned rail back from
 * the activity view, is simply there.
 *
 * Given `outer`, the groups the elements sit in (the roster's sections) slide
 * too, first; an element inside a group that moved takes only its own move
 * on top of its group's, so a section's bots ride with it rather than sliding
 * twice.
 */
export const FLIP_ID = "flip";
const FLIP_MS = 240;
const LEAVE_MS = 170;
const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)";
const EASE_IN = "cubic-bezier(0.4, 0, 1, 1)";

interface Place {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly node: HTMLElement;
}

/** Where an element lies in the container's layout, before any transform; null when it is not drawn. */
function layoutPlace(node: HTMLElement, container: HTMLElement): { x: number; y: number } | null {
  let x = 0;
  let y = 0;
  let at: Element | null = node;
  while (at !== container) {
    if (!(at instanceof HTMLElement) || !container.contains(at)) return null;
    x += at.offsetLeft;
    y += at.offsetTop;
    at = at.offsetParent;
  }
  return { x, y };
}

/** Slides each element marked with `attribute` from where it was to where it is; the groups' moves, by element, for what they hold. */
function slide(box: HTMLElement, attribute: string, places: Map<string, Place>, still: boolean, carried: ReadonlyMap<Element, { dx: number; dy: number }>, outer: string | undefined): { next: Map<string, Place>; moved: Map<Element, { dx: number; dy: number }>; fresh: HTMLElement[] } {
  const next = new Map<string, Place>();
  const moved = new Map<Element, { dx: number; dy: number }>();
  const fresh: HTMLElement[] = [];
  for (const node of box.querySelectorAll<HTMLElement>(`[${attribute}]`)) {
    const id = node.getAttribute(attribute) ?? "";
    if (node.offsetParent == null) continue;
    const at = layoutPlace(node, box);
    if (at == null) continue;
    next.set(id, { ...at, width: node.offsetWidth, height: node.offsetHeight, node });
    if (still) continue;
    const was = places.get(id);
    if (was == null) {
      fresh.push(node);
      continue;
    }
    const group = outer == null ? null : node.parentElement?.closest(`[${outer}]`);
    const ride = group == null ? undefined : carried.get(group);
    const dx = was.x - at.x - (ride?.dx ?? 0);
    const dy = was.y - at.y - (ride?.dy ?? 0);
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
    for (const running of node.getAnimations()) if (running.id === FLIP_ID) running.cancel();
    const move = node.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }], { duration: FLIP_MS, easing: EASE_OUT });
    move.id = FLIP_ID;
    moved.set(node, { dx: was.x - at.x, dy: was.y - at.y });
  }
  return { next, moved, fresh };
}

export function useFlip(container: React.RefObject<HTMLElement | null>, attribute: string, ids?: readonly string[], outer?: string): void {
  const places = useRef(new Map<string, Place>());
  const groups = useRef(new Map<string, Place>());
  const known = useRef<ReadonlySet<string> | null>(null);
  const width = useRef<number | null>(null);
  useLayoutEffect(() => {
    const before = known.current;
    if (ids != null) known.current = new Set(ids);
    const box = container.current;
    if (box == null) {
      places.current.clear();
      groups.current.clear();
      width.current = null;
      return;
    }
    const resized = width.current != null && width.current !== box.clientWidth;
    width.current = box.clientWidth;
    const still = resized || window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const outside = outer == null ? null : slide(box, outer, groups.current, still, new Map(), undefined);
    if (outside != null) groups.current = outside.next;
    const { next, fresh } = slide(box, attribute, places.current, still, outside?.moved ?? new Map(), outer);
    if (ids != null && before != null) {
      for (const node of fresh) {
        if (before.has(node.getAttribute(attribute) ?? "")) continue;
        node.animate([{ opacity: 0, transform: "translateY(6px) scale(0.6)" }, { opacity: 1, transform: "none" }], { duration: FLIP_MS, easing: EASE_OUT });
      }
    }
    if (ids != null && !still) {
      for (const [id, was] of places.current) {
        if (next.has(id) || known.current?.has(id) === true) continue;
        const ghost = was.node.cloneNode(true) as HTMLElement;
        ghost.removeAttribute(attribute);
        ghost.setAttribute("aria-hidden", "true");
        ghost.inert = true;
        ghost.classList.add("leaving");
        Object.assign(ghost.style, { position: "absolute", margin: "0", pointerEvents: "none", left: `${was.x}px`, top: `${was.y}px`, width: `${was.width}px`, height: `${was.height}px` });
        box.append(ghost);
        ghost.animate([{ opacity: 1, transform: "none" }, { opacity: 0, transform: "translateY(4px) scale(0.6)" }], { duration: LEAVE_MS, easing: EASE_IN, fill: "forwards" }).onfinish = () => ghost.remove();
      }
    }
    places.current = next;
  });
}
